'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** How long "Gespeichert" stays up before falling back to the neutral hint. */
const SAVED_NOTICE_MS = 3000;

interface AutoSaveStatusValue {
    status: AutoSaveStatus;
    setStatus: (status: AutoSaveStatus) => void;
}

const AutoSaveStatusContext = createContext<AutoSaveStatusValue | null>(null);

/**
 * Carries the auto-save state from the form down to the panel chrome that shows it.
 *
 * Lives in @ui rather than next to SettingsForm so the UI layer does not have to
 * import app code — SettingsForm pulls the provider in, not the other way round.
 */
export function AutoSaveStatusProvider({ children }: { children: ReactNode }) {
    const [status, setStatusState] = useState<AutoSaveStatus>('idle');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // A save flushed on unmount still reports its result back here.
    const mountedRef = useRef(true);

    const setStatus = useCallback((next: AutoSaveStatus) => {
        if (!mountedRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        setStatusState(next);

        // "saving" waits for its result, "error" stays until the next attempt.
        if (next === 'saved') {
            timerRef.current = setTimeout(() => setStatusState('idle'), SAVED_NOTICE_MS);
        }
    }, []);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const value = useMemo(() => ({ status, setStatus }), [status, setStatus]);

    return <AutoSaveStatusContext.Provider value={value}>{children}</AutoSaveStatusContext.Provider>;
}

/** Falls back to "idle" outside a provider, so panels without auto-save render unchanged. */
export function useAutoSaveStatus(): AutoSaveStatusValue {
    return useContext(AutoSaveStatusContext) ?? { status: 'idle', setStatus: () => {} };
}
